/**
 * Cognito authentication service
 * Used when REACT_APP_COGNITO_USER_POOL_ID and REACT_APP_COGNITO_CLIENT_ID are set
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { cognitoConfig } from '../config/cognito';

function getUserPool(): CognitoUserPool {
  if (!cognitoConfig.userPoolId || !cognitoConfig.clientId) {
    throw new Error('Cognito is not configured. Set REACT_APP_COGNITO_USER_POOL_ID and REACT_APP_COGNITO_CLIENT_ID.');
  }
  return new CognitoUserPool({
    UserPoolId: cognitoConfig.userPoolId,
    ClientId: cognitoConfig.clientId,
  });
}

function getCognitoUser(username: string): CognitoUser {
  return new CognitoUser({
    Username: username,
    Pool: getUserPool(),
  });
}

export interface CognitoSignUpResult {
  user: CognitoUser;
  userSub: string;
}

export const cognitoAuthService = {
  /**
   * Sign up: creates Cognito user, sends verification code. Does not authenticate or issue tokens.
   */
  signUp(email: string, password: string, name: string): Promise<CognitoSignUpResult> {
    return new Promise((resolve, reject) => {
      const pool = getUserPool();
      const attributes = [
        new CognitoUserAttribute({ Name: 'email', Value: email }),
        new CognitoUserAttribute({ Name: 'name', Value: name }),
        new CognitoUserAttribute({ Name: 'preferred_username', Value: email }),
      ];
      pool.signUp(email, password, attributes, [], (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        if (!result?.user) {
          reject(new Error('Sign up failed'));
          return;
        }
        resolve({
          user: result.user,
          userSub: result.userSub,
        });
      });
    });
  },

  /**
   * Confirm registration with the code sent to the user's email (required when email verification is on)
   */
  confirmRegistration(email: string, code: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const user = getCognitoUser(email);
      user.confirmRegistration(code, true, (err) => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      });
    });
  },

  /**
   * Resend verification code to email
   */
  resendConfirmationCode(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const user = getCognitoUser(email);
      user.resendConfirmationCode((err) => {
        if (err) reject(err);
        else resolve();
      });
    });
  },

  /**
   * Sign in and return Id Token (for API Authorization header)
   */
  signIn(email: string, password: string): Promise<{ idToken: string; session: CognitoUserSession }> {
    return new Promise((resolve, reject) => {
      const authDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });
      const user = getCognitoUser(email);
      user.authenticateUser(authDetails, {
        onSuccess: (session) => {
          const idToken = session.getIdToken().getJwtToken();
          resolve({ idToken, session });
        },
        onFailure: (err) => reject(err),
        newPasswordRequired: () => {
          reject(new Error('New password required. Please use the forgot password flow.'));
        },
      });
    });
  },

  /**
   * Get current session and Id Token (if logged in)
   */
  getCurrentSession(): Promise<CognitoUserSession> {
    return new Promise((resolve, reject) => {
      const pool = getUserPool();
      const user = pool.getCurrentUser();
      if (!user) {
        reject(new Error('No current user'));
        return;
      }
      user.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err) {
          reject(err);
          return;
        }
        if (!session?.isValid()) {
          reject(new Error('Session invalid'));
          return;
        }
        resolve(session);
      });
    });
  },

  /**
   * Get Id Token string for API calls (from memory or localStorage-backed session)
   */
  getIdToken(): Promise<string> {
    return this.getCurrentSession().then((session) => session.getIdToken().getJwtToken());
  },

  /**
   * Sign out current user
   */
  signOut(): void {
    const pool = getUserPool();
    const user = pool.getCurrentUser();
    if (user) {
      user.signOut();
    }
  },

  /**
   * Request a password reset code (sends to user's email)
   */
  forgotPasswordRequest(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const user = getCognitoUser(email);
      user.forgotPassword({
        onSuccess: () => resolve(),
        onFailure: (err) => reject(err),
      });
    });
  },

  /**
   * Confirm forgot password with code and new password
   */
  confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const user = getCognitoUser(email);
      user.confirmPassword(code, newPassword, {
        onSuccess: () => resolve(),
        onFailure: (err) => reject(err),
      });
    });
  },

  /**
   * Get current Cognito username (email) if session exists
   */
  getCurrentUsername(): Promise<string | null> {
    return new Promise((resolve) => {
      const pool = getUserPool();
      const user = pool.getCurrentUser();
      if (!user) {
        resolve(null);
        return;
      }
      user.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session?.isValid()) {
          resolve(null);
          return;
        }
        resolve(user.getUsername());
      });
    });
  },
};
